import React from 'react';
import { useIntl } from 'react-intl';
import type { ResponsiveImageOutput } from 'contracts';
import BackgroundImage from 'components/background-image';
import CardCarousel, { type SwipeDirection } from 'components/card-carousel';
import Html from 'components/html';
import Icon, { IconList } from 'components/icon';
import NavigationDots from 'components/navigation-dots';
import { sendTracking } from 'services/google-analytics';
import { noDigitsCurrencyFormat } from 'utils/format-number';
import { throttle } from 'throttle-debounce';
import useMobileView from 'enhancers/use-mobile-view';

import YearsLogo from './assets/25-years.svg';
import JoinUs from './assets/join-us.jpg';
import JoinUsMobile from './assets/join-us-mobile.jpg';
import Sparkle from './assets/sparkle.svg';
import ParticlesBg from './particles-bg';
import tracking from './why-join-banner.tracking';
import { config, infoCards, testId } from './why-join-banner.settings';
import messages from './why-join-banner.i18n';
import styles from './why-join-banner.module.scss';

const WhyJoinBanner: React.FunctionComponent<unknown> = () => {
  const isMobileView = useMobileView();
  const [selectedCard, setSelectedCard] = React.useState(0);
  const titleRef = React.useRef<HTMLDivElement>(null);
  const carouselContainerRef = React.useRef<HTMLDivElement>(null);
  const carouselRef = React.useRef<HTMLDivElement>(null);
  const cardRefs = React.useRef<HTMLDivElement[]>([]);
  const { formatMessage, formatNumber } = useIntl();
  const translation = messages.pages.landing.whyJoinBanner.livejasmin;

  const handleGoPrevious = React.useCallback(() => {
    sendTracking(tracking.previousCard);
    setSelectedCard((prev) => {
      if (prev > 0) return prev - 1;

      return infoCards.length - 1;
    });
  }, []);

  const handleGoNext = React.useCallback(() => {
    sendTracking(tracking.nextCard);
    setSelectedCard((prev) => {
      if (prev < infoCards.length - 1) return prev + 1;

      return 0;
    });
  }, []);

  const handleCardSwipe = React.useCallback(
    (direction: SwipeDirection) => {
      sendTracking(tracking.mobileCardSwipeNav, { direction });

      if (direction === 'left') {
        handleGoPrevious();

        return;
      }

      handleGoNext();
    },
    [handleGoPrevious, handleGoNext]
  );

  const handleSliderDotNav = React.useCallback((index: number) => {
    sendTracking(tracking.sliderDotNav);
    setSelectedCard(index);
  }, []);

  const initializeCardRef = React.useCallback(
    (el: HTMLDivElement, index: number) => () => {
      cardRefs.current[index] = el;
    },
    []
  );

  const setCardsHeight = React.useCallback(() => {
    if (!cardRefs.current || !carouselContainerRef.current || !carouselRef.current || !titleRef.current) {
      return;
    }

    // Reset any currently-set inline styles
    titleRef.current.style.removeProperty('margin-bottom');
    carouselContainerRef.current.style.removeProperty('margin-bottom');
    carouselRef.current.style.removeProperty('height');
    cardRefs.current.forEach((card) => {
      card.style.removeProperty('height');
    });

    // Determine the tallest card height
    const tallestCardHeight = Math.max(
      ...cardRefs.current.map((card) => {
        return card.offsetHeight;
      })
    );

    // Conditionally add spacing to navigation dots in mobile view, or carousel top and bottom in desktop view
    if (isMobileView) {
      carouselRef.current.setAttribute('style', `height: ${tallestCardHeight}px`);
    } else if (tallestCardHeight > config.defaultCardHeight) {
      const titleMarginBottom = parseInt(
        window.getComputedStyle(titleRef.current).getPropertyValue('margin-bottom'),
        10
      );

      titleRef.current.setAttribute(
        'style',
        `margin-bottom: ${titleMarginBottom + tallestCardHeight - config.defaultCardHeight}px`
      );

      carouselContainerRef.current.setAttribute(
        'style',
        `margin-bottom: ${(tallestCardHeight - config.defaultCardHeight) / 2}px`
      );
    }

    // Set all cards to the height of the tallest card
    cardRefs.current.forEach((card) => {
      card.setAttribute('style', `height: ${tallestCardHeight}px`);
    });
  }, [isMobileView]);

  React.useLayoutEffect(() => {
    const handleWindowResize = throttle(50, (): void => {
      setCardsHeight();
    });

    window.addEventListener('resize', handleWindowResize);

    return () => {
      window.removeEventListener('resize', handleWindowResize);
    };
  }, [setCardsHeight]);

  setTimeout(setCardsHeight, 500);

  const renderCards = React.useCallback(() => {
    return infoCards.map((cardMeta, index) => {
      return (
        <Html.div
          className={[styles.whyJoinCard, selectedCard === index && styles.active, 'py-8']}
          key={cardMeta.key}
          ref={(el) => initializeCardRef(el as HTMLDivElement, index)}
        >
          <Html.div className={[styles.content, 'w-100']}>
            <Html.h3 className={[styles.title, 'mb-6 mb-md-4']}>{formatMessage(cardMeta.title)}</Html.h3>
            <Html.ul className={styles.list}>
              {cardMeta.bullets.map((bullet, bulletIndex) => (
                // eslint-disable-next-line react/no-array-index-key
                <Html.li className={styles.item} key={`bullet-${bulletIndex}`}>
                  <Icon name={bullet.icon} />
                  {formatMessage(bullet.text, {
                    numMonths: config.numMonthsTrafficBoost,
                    amountPrizes: formatNumber(config.monthlyCashPrizes, noDigitsCurrencyFormat),
                  })}
                </Html.li>
              ))}
            </Html.ul>
          </Html.div>
        </Html.div>
      );
    });
  }, [selectedCard, formatMessage, formatNumber, initializeCardRef]);

  return (
    <Html.div className={[styles.whyJoinBanner, 'w-100 mb-0 pb-10']} testId={testId.component}>
      <Html.div className={[styles.top, 'w-100']}>
        <BackgroundImage
          className={[styles.bg, 'w-100 h-100']}
          src={JoinUs as ResponsiveImageOutput}
          mobileSrc={JoinUsMobile as ResponsiveImageOutput}
        />
        <Html.div className={styles.shade} />
        <ParticlesBg className={styles.particles} />
        <Html.div className="position-relative">
          <YearsLogo className={styles.yearsLogo} />
          <Sparkle className={styles.sparkle} />
        </Html.div>
        <Html.div className={[styles.title]} ref={titleRef}>
          {formatMessage(translation.whyJoinUs.title, {
            b: (text: React.ReactNode) => <Html.b className={styles.highlight}>{text}</Html.b>,
          })}
        </Html.div>
      </Html.div>
      <Html.div className={[styles.cardCarouselContainer, 'w-100']} ref={carouselContainerRef}>
        <Html.button
          type="button"
          className={[styles.navButton, styles.navLeft, 'd-none d-md-block']}
          onClick={handleGoPrevious}
          testId={testId.navLeft}
        >
          <Icon name={IconList.arrowLeftSolid} className={styles.navIcon} />
        </Html.button>
        <CardCarousel
          ref={carouselRef}
          className={[styles.cardCarousel, 'w-100']}
          selected={selectedCard}
          onSwipe={handleCardSwipe}
          testId={testId.cardCarousel}
        >
          {renderCards()}
        </CardCarousel>
        <Html.button
          type="button"
          className={[styles.navButton, styles.navRight, 'd-none d-md-block']}
          onClick={handleGoNext}
          testId={testId.navRight}
        >
          <Icon name={IconList.arrowRightSolid} className={styles.navIcon} />
        </Html.button>
        <NavigationDots
          activeIndex={selectedCard}
          total={infoCards.length}
          className={[styles.navDots, 'd-md-none d-flex mt-2']}
          onClick={handleSliderDotNav}
          theme="dark"
        />
      </Html.div>
    </Html.div>
  );
};

export default WhyJoinBanner;
